import { Story } from '../../types/story';

export const dublinPub: Story = {
  id: 'dublin-pub',
  title: 'Last Call',
  excerpt: 'A Dublin pub after hours becomes the setting for an unexpected connection between a bartender and a regular.',
  city: 'Dublin',
  publishDate: '2025-12-29',
  readTime: '10 min read',
  tags: ['♀♂', 'pub', 'after-hours', 'intimate'],
  categories: ['romantic'],
  intensity: 2,
  content: `I'd been coming to O'Malley's for three months, ever since I moved to Dublin for work. It was a proper pub—dark wood, Guinness on tap, locals who eyed newcomers with friendly suspicion until you proved you weren't a tourist.

Siobhan was the bartender most nights. Red hair, sharp wit, hands that moved with practiced efficiency as she pulled pints and traded banter with regulars. I'd developed a crush immediately, though I'd never said more than "Another Guinness, please" and "Cheers."

It was a Tuesday night, raining hard outside, when I found myself the last customer at closing time.

"You don't have to go home," Siobhan said, wiping down the bar, "but you can't stay here."

"Right, sorry." I drained the last of my pint, reached for my coat.

"Unless..." She paused, looked at me properly for the first time in three months. "Unless you want to help me close up. I'll pour you a proper drink. Not that Guinness shite you've been nursing all night."

"I thought Guinness was sacred in Ireland."

"Tourist propaganda. The good stuff's in the back."

I followed her behind the bar, into the small office where she kept the accounts and, apparently, a collection of whiskeys that would make a collector weep.

"Redbreast 21," she said, pouring two generous measures. "None of that ice nonsense."

We sat on the worn leather couch in her office, rain drumming on the windows, the pub quiet around us.

"So," she said. "Three months you've been coming here. Same time, same seat, same drink. What's your story?"

"No story. Just needed a place that felt like home."

"And does it?"

"Starting to."

She studied me over her glass. "You're lonely."

"Observant."

"It's my job. I watch people. You come in alone, leave alone, never talk to anyone but me. And barely that."

"Maybe I like the quiet."

"Maybe. Or maybe you're afraid if you start talking, you'll say too much."

The whiskey was making me bold. "What would I say?"

"That you moved here to escape something. That you thought a new city would fix whatever was broken. That you're starting to realize geography doesn't heal wounds."

I stared at her. "Fuck. Do you read everyone like this?"

"Only the interesting ones." She refilled our glasses. "Want to know what else I see?"

"I'm not sure."

"You watch me. Not in a creepy way. In a... hopeful way. Like you're trying to figure out if I'm real or just another fantasy you'll leave behind when you move to the next city."

"I'm not planning to leave."

"Everyone plans to leave Dublin. It's too small, too rainy, too expensive. But some of us stay anyway."

"Why do you stay?"

"Because leaving is easy. Staying is the brave thing."

We sat in silence for a moment, the whiskey warm in my chest, her words warmer.

"Can I tell you something?" I asked.

"That's why I poured the good stuff."

"I come here because of you. Not the pub, not the Guinness. You. The way you laugh at terrible jokes, the way you remember everyone's drink, the way you make this place feel like it matters."

She set down her glass. "That's the whiskey talking."

"No. That's three months of watching you and being too scared to say anything talking."

"And what changed tonight?"

"You asked me to stay."

She moved closer, close enough that I could smell her perfume under the scent of beer and wood polish. "I've been waiting three months for you to say something. Anything. I was starting to think you were just really committed to being mysterious."

"I'm not mysterious. I'm just bad at this."

"At what?"

"Telling someone I think about them constantly. That I time my visits to your shifts. That I've imagined this conversation a hundred times."

"And how did it go in your imagination?"

"Better. I was more articulate. You were more impressed."

She laughed, a real laugh, not the professional one she used with customers. "I'm plenty impressed. You just told me you've been pining for three months. That's either romantic or slightly stalkerish."

"Which is it?"

"Depends on what you do next."

I kissed her. Or she kissed me. It was hard to tell who moved first, but suddenly we were tangled together on the couch, whiskey forgotten, the rain outside matching the intensity inside.

Her hands were in my hair, my hands under her shirt, both of us moving with the urgency of three months of unspoken tension finally finding release.

"Wait," she gasped, pulling back. "Not here. Not on this couch. Do you know how many drunk people have passed out on this thing?"

I laughed, breathless. "Where then?"

"My flat. It's above the pub. But you have to promise me something."

"Anything."

"This isn't just tonight. I don't do one-night stands with customers. It's messy."

"I don't want just tonight."

"Good. Because I've been imagining this for three months too."

Her flat was small, cozy, filled with books and plants and the kind of comfortable chaos that comes from actually living in a space. We barely made it to the bedroom.

She was confident, directing me with the same efficiency she used behind the bar, telling me what she wanted, showing me what she liked. It was intense and intimate and nothing like the fantasy versions I'd imagined.

It was better.

Afterward, we lay tangled in her sheets, the rain still falling, the pub quiet below us.

"So," she said, tracing patterns on my chest. "Same time tomorrow?"

"For a drink?"

"For whatever comes after the drink."

"I can do that."

"Good. Because I'm not done with you yet. Three months of buildup deserves more than one night."

I stayed until morning, and the morning after that, and eventually stopped going home at all. Six months later, my name was on the lease. A year later, I was helping her run the pub.

Sometimes tourists ask how we met, expecting some romantic story about love at first sight. We just smile and say we met at work. The truth—that it took three months of silent longing and a bottle of Redbreast 21 to finally speak—is ours alone.

And on quiet Tuesday nights, when the rain is falling and the pub is empty, we lock the door early and pour the good whiskey, remembering the night everything changed.`
};
